// Pure selection logic for the per-site Updates tab. Kept side-effect-free so
// the partitioning rules are unit-testable without rendering anything.

import type { SiteStatus, JobKind, FleetUpdatesRequest } from '../../../lib/types';

export interface PendingRow {
  /** Selection key. Unique across kinds ('core' is both a slug and a kind). */
  key: string;
  slug: string;
  name: string;
  kind: 'plugin' | 'theme' | 'core';
  from: string;
  to: string;
  isEm: boolean;
}

export function derivePending(s: SiteStatus): PendingRow[] {
  const rows: PendingRow[] = [];
  if (s.coreUpdateVersion) {
    rows.push({
      key: 'core:core', slug: 'core', name: 'Core', kind: 'core',
      from: s.coreVersion, to: s.coreUpdateVersion, isEm: false,
    });
  }
  for (const p of s.plugins) {
    if (p.updateVersion) {
      rows.push({
        key: `plugin:${p.slug}`, slug: p.slug, name: p.name, kind: 'plugin',
        from: p.version, to: p.updateVersion, isEm: p.isEm,
      });
    }
  }
  for (const t of s.themes) {
    if (t.updateVersion) {
      rows.push({
        key: `theme:${t.slug}`, slug: t.slug, name: t.name, kind: 'theme',
        from: t.version, to: t.updateVersion, isEm: false,
      });
    }
  }
  return rows;
}

// Selected rows → one request PER KIND (gateway rejects a mixed job), in apply
// order plugins → theme → core, so core never lands against stale plugins.
// updates.theme/updates.core take no slugs — each manages the one PUC-managed
// theme/running core, not a selectable list.
export function buildRequests(
  siteId: string,
  rows: PendingRow[],
  selected: Set<string>,
): Array<{ kind: JobKind; body: FleetUpdatesRequest }> {
  const chosen = rows.filter((r) => selected.has(r.key));
  const out: Array<{ kind: JobKind; body: FleetUpdatesRequest }> = [];

  const slugs = chosen.filter((r) => r.kind === 'plugin').map((r) => r.slug);
  if (slugs.length > 0) {
    out.push({ kind: 'updates.plugins', body: { updates: [{ siteId, slugs }], kind: 'updates.plugins' } });
  }
  if (chosen.some((r) => r.kind === 'theme')) {
    out.push({ kind: 'updates.theme', body: { updates: [{ siteId, slugs: [] }], kind: 'updates.theme' } });
  }
  if (chosen.some((r) => r.kind === 'core')) {
    out.push({ kind: 'updates.core', body: { updates: [{ siteId, slugs: [] }], kind: 'updates.core' } });
  }
  return out;
}

/** The cache-purge request for one site (no slugs — the kind IS the action). */
export function buildCachePurgeRequest(siteId: string): FleetUpdatesRequest {
  return { updates: [{ siteId, slugs: [] }], kind: 'cache.purge' };
}
